// ============================================================================
// PARTICLE SYSTEM - Cyberpunk-style visual effects
// ============================================================================

import { Vector2 } from '@/shared/types';
import { Vec2 } from '@/shared/utils';
import { Renderer } from '@/engine/Renderer';
import { COLORS } from '@/shared/constants';

// ----------------------------------------------------------------------------
// PARTICLE TYPES
// ----------------------------------------------------------------------------

export interface Particle {
  position: Vector2;
  velocity: Vector2;
  acceleration: Vector2;
  color: string;
  size: number;
  life: number;
  maxLife: number;
  rotation: number;
  rotationSpeed: number;
  type: ParticleType;
  trail: Vector2[] | undefined;  // For trail effects
}

export enum ParticleType {
  Spark = 'spark',
  Smoke = 'smoke',
  Blood = 'blood',
  Debris = 'debris',
  Energy = 'energy',
  Glitch = 'glitch',
  Data = 'data',
}

export interface ParticleConfig {
  count: number;
  position: Vector2;
  direction?: number;  // Base direction in radians
  spread?: number;     // Spread angle in radians
  speed: { min: number; max: number };
  size: { min: number; max: number };
  life: { min: number; max: number };
  colors: string[];
  type: ParticleType;
  gravity?: number;
  friction?: number;
  rotationSpeed?: { min: number; max: number };
  trail?: boolean;
}

// ----------------------------------------------------------------------------
// PARTICLE SYSTEM
// ----------------------------------------------------------------------------

export class ParticleSystem {
  private particles: Particle[] = [];
  private maxParticles: number = 1000;

  update(dt: number): void {
    const dtSec = dt / 1000;

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      
      // Update life
      p.life -= dtSec;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      // Store trail position
      if (p.trail) {
        p.trail.push({ ...p.position });
        if (p.trail.length > 10) {
          p.trail.shift();
        }
      }

      // Update velocity with acceleration
      p.velocity = Vec2.add(p.velocity, Vec2.mul(p.acceleration, dtSec));
      
      // Update position
      p.position = Vec2.add(p.position, Vec2.mul(p.velocity, dtSec));
      
      // Update rotation
      p.rotation += p.rotationSpeed * dtSec;
    }
  }

  render(renderer: Renderer): void {
    for (const p of this.particles) {
      const lifeRatio = p.life / p.maxLife;
      const alpha = Math.min(1, lifeRatio * 2); // Fade out in last 50%

      switch (p.type) {
        case ParticleType.Spark:
          this.renderSpark(renderer, p, alpha);
          break;
        case ParticleType.Smoke:
          this.renderSmoke(renderer, p, alpha);
          break;
        case ParticleType.Debris:
          this.renderDebris(renderer, p, alpha);
          break;
        case ParticleType.Energy:
          this.renderEnergy(renderer, p, alpha);
          break;
        case ParticleType.Glitch:
          this.renderGlitch(renderer, p, alpha);
          break;
        case ParticleType.Data:
          this.renderData(renderer, p, alpha);
          break;
        case ParticleType.Blood:
          this.renderBlood(renderer, p, alpha);
          break;
      }
    }
  }

  private renderSpark(renderer: Renderer, p: Particle, alpha: number): void {
    const color = this.colorWithAlpha(p.color, alpha);
    
    // Draw spark line based on velocity direction
    const tailLength = Vec2.length(p.velocity) * 0.03;
    const tail = Vec2.normalize(p.velocity);
    const tailEnd = Vec2.sub(p.position, Vec2.mul(tail, tailLength));
    
    renderer.drawLine(
      tailEnd.x, tailEnd.y,
      p.position.x, p.position.y,
      { color, width: p.size }
    );
    
    // Bright head
    renderer.drawCircle(p.position.x, p.position.y, p.size * 0.5, {
      fill: color,
      glow: true,
      glowColor: p.color,
      glowSize: p.size * 2,
    });
  }

  private renderSmoke(renderer: Renderer, p: Particle, alpha: number): void {
    const size = p.size * (1 + (1 - p.life / p.maxLife) * 2);
    const color = this.colorWithAlpha(p.color, alpha * 0.5);
    
    renderer.drawCircle(p.position.x, p.position.y, size, {
      fill: color,
    });
  }

  private renderDebris(renderer: Renderer, p: Particle, alpha: number): void {
    const color = this.colorWithAlpha(p.color, alpha);
    const size = p.size;
    
    // Draw rotating square debris
    const ctx = renderer['ctx'] as CanvasRenderingContext2D;
    ctx.save();
    ctx.translate(p.position.x, p.position.y);
    ctx.rotate(p.rotation);
    ctx.fillStyle = color;
    ctx.fillRect(-size / 2, -size / 2, size, size);
    ctx.restore();
  }

  private renderEnergy(renderer: Renderer, p: Particle, alpha: number): void {
    const color = this.colorWithAlpha(p.color, alpha);
    
    renderer.drawCircle(p.position.x, p.position.y, p.size, {
      fill: color,
      glow: true,
      glowColor: p.color,
      glowSize: p.size * 3,
    });
    
    // Draw trail
    if (p.trail && p.trail.length > 1) {
      for (let i = 1; i < p.trail.length; i++) {
        const prev = p.trail[i - 1]!;
        const curr = p.trail[i]!;
        const trailAlpha = (i / p.trail.length) * alpha * 0.5;
        renderer.drawLine(
          prev.x, prev.y,
          curr.x, curr.y,
          { color: this.colorWithAlpha(p.color, trailAlpha), width: p.size * 0.5 }
        );
      }
    }
  }

  private renderGlitch(renderer: Renderer, p: Particle, alpha: number): void {
    const color = this.colorWithAlpha(p.color, alpha);
    
    // Glitch rectangle with offset
    const offsetX = (Math.random() - 0.5) * 10;
    const offsetY = (Math.random() - 0.5) * 5;
    const width = p.size * 2 + Math.random() * 10;
    const height = p.size * 0.5;
    
    const ctx = renderer['ctx'] as CanvasRenderingContext2D;
    ctx.fillStyle = color;
    ctx.fillRect(
      p.position.x + offsetX - width / 2,
      p.position.y + offsetY - height / 2,
      width,
      height
    );
  }

  private renderData(renderer: Renderer, p: Particle, alpha: number): void {
    const color = this.colorWithAlpha(p.color, alpha);
    
    // Draw random binary character
    const char = Math.random() > 0.5 ? '1' : '0';
    const ctx = renderer['ctx'] as CanvasRenderingContext2D;
    ctx.font = `${p.size * 2}px monospace`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(char, p.position.x, p.position.y);
  }

  private renderBlood(renderer: Renderer, p: Particle, alpha: number): void {
    const size = p.size * (1 + (1 - p.life / p.maxLife) * 0.5);
    const color = this.colorWithAlpha(p.color, alpha * 0.8);
    
    renderer.drawCircle(p.position.x, p.position.y, size, {
      fill: color,
    });
  }

  emit(config: ParticleConfig): void {
    const { 
      count, 
      position, 
      direction = 0, 
      spread = Math.PI * 2, 
      speed, 
      size, 
      life, 
      colors, 
      type,
      gravity = 0,
      friction = 0,
      rotationSpeed = { min: 0, max: 0 },
      trail = false,
    } = config;

    for (let i = 0; i < count && this.particles.length < this.maxParticles; i++) {
      const angle = direction + (Math.random() - 0.5) * spread;
      const spd = speed.min + Math.random() * (speed.max - speed.min);
      const sz = size.min + Math.random() * (size.max - size.min);
      const lf = life.min + Math.random() * (life.max - life.min);
      const color = colors[Math.floor(Math.random() * colors.length)]!;
      const rotSpd = rotationSpeed.min + Math.random() * (rotationSpeed.max - rotationSpeed.min);

      this.particles.push({
        position: { ...position },
        velocity: Vec2.mul(Vec2.fromAngle(angle), spd),
        acceleration: { x: friction * -1, y: gravity },
        color,
        size: sz,
        life: lf,
        maxLife: lf,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: rotSpd,
        type,
        trail: trail ? [] : undefined,
      });
    }
  }

  // ----------------------------------------------------------------------------
  // PRESET EFFECTS
  // ----------------------------------------------------------------------------

  bulletImpact(position: Vector2, normal: Vector2): void {
    const angle = Math.atan2(normal.y, normal.x);
    
    // Sparks
    this.emit({
      count: 8,
      position,
      direction: angle,
      spread: Math.PI * 0.6,
      speed: { min: 100, max: 300 },
      size: { min: 1, max: 3 },
      life: { min: 0.1, max: 0.3 },
      colors: [COLORS.CYAN, COLORS.YELLOW, '#ffffff'],
      type: ParticleType.Spark,
      gravity: 300,
    });

    // Small smoke puff
    this.emit({
      count: 3,
      position,
      spread: Math.PI * 2,
      speed: { min: 20, max: 50 },
      size: { min: 3, max: 6 },
      life: { min: 0.2, max: 0.4 },
      colors: ['#666666', '#888888', '#aaaaaa'],
      type: ParticleType.Smoke,
      gravity: -50,
    });
  }

  bulletHitPlayer(position: Vector2, direction: Vector2): void {
    const angle = Math.atan2(direction.y, direction.x);
    
    // Energy sparks
    this.emit({
      count: 12,
      position,
      direction: angle + Math.PI,
      spread: Math.PI * 0.8,
      speed: { min: 80, max: 200 },
      size: { min: 2, max: 4 },
      life: { min: 0.15, max: 0.35 },
      colors: [COLORS.MAGENTA, COLORS.CYAN, '#ff4488'],
      type: ParticleType.Energy,
      gravity: 100,
      trail: true,
    });
  }

  playerDeath(position: Vector2): void {
    // Cyberpunk death explosion!
    
    // Energy burst
    this.emit({
      count: 30,
      position,
      spread: Math.PI * 2,
      speed: { min: 150, max: 400 },
      size: { min: 3, max: 8 },
      life: { min: 0.3, max: 0.8 },
      colors: [COLORS.CYAN, COLORS.MAGENTA, '#ff4488', '#44ffaa'],
      type: ParticleType.Energy,
      gravity: 200,
      trail: true,
    });

    // Glitch rectangles
    this.emit({
      count: 20,
      position,
      spread: Math.PI * 2,
      speed: { min: 50, max: 150 },
      size: { min: 4, max: 12 },
      life: { min: 0.2, max: 0.5 },
      colors: [COLORS.CYAN, COLORS.MAGENTA, '#ffffff'],
      type: ParticleType.Glitch,
    });

    // Data fragments (binary)
    this.emit({
      count: 15,
      position,
      spread: Math.PI * 2,
      speed: { min: 100, max: 250 },
      size: { min: 6, max: 12 },
      life: { min: 0.4, max: 1.0 },
      colors: [COLORS.CYAN, '#00ff88', COLORS.MAGENTA],
      type: ParticleType.Data,
      gravity: 150,
    });

    // Debris
    this.emit({
      count: 15,
      position,
      spread: Math.PI * 2,
      speed: { min: 100, max: 300 },
      size: { min: 3, max: 8 },
      life: { min: 0.3, max: 0.7 },
      colors: ['#333344', '#444455', '#555566', '#2a2a3a'],
      type: ParticleType.Debris,
      gravity: 500,
      rotationSpeed: { min: -10, max: 10 },
    });
  }

  weaponSwitch(position: Vector2): void {
    // Subtle energy effect
    this.emit({
      count: 8,
      position,
      spread: Math.PI * 2,
      speed: { min: 30, max: 80 },
      size: { min: 2, max: 4 },
      life: { min: 0.15, max: 0.3 },
      colors: [COLORS.CYAN, COLORS.YELLOW],
      type: ParticleType.Energy,
    });
  }

  muzzleFlashParticles(position: Vector2, direction: number): void {
    // Hot sparks from muzzle
    this.emit({
      count: 5,
      position,
      direction,
      spread: Math.PI * 0.3,
      speed: { min: 200, max: 400 },
      size: { min: 1, max: 2 },
      life: { min: 0.05, max: 0.15 },
      colors: [COLORS.YELLOW, '#ffaa00', '#ffffff'],
      type: ParticleType.Spark,
      gravity: 300,
    });
  }

  // -------------------------------------------------------------------------
  // ABILITY EFFECTS
  // -------------------------------------------------------------------------

  /** Blink/teleport effect - flash at position */
  blinkEffect(position: Vector2): void {
    // Bright flash
    this.emit({
      count: 20,
      position,
      spread: Math.PI * 2,
      speed: { min: 100, max: 300 },
      size: { min: 3, max: 8 },
      life: { min: 0.2, max: 0.5 },
      colors: [COLORS.CYAN, COLORS.MAGENTA, '#ffffff'],
      type: ParticleType.Energy,
    });
    
    // Data fragments
    this.emit({
      count: 10,
      position,
      spread: Math.PI * 2,
      speed: { min: 50, max: 150 },
      size: { min: 4, max: 8 },
      life: { min: 0.3, max: 0.6 },
      colors: [COLORS.CYAN, '#00ffaa'],
      type: ParticleType.Data,
    });
  }

  /** Trail between two points for blink */
  blinkTrail(start: Vector2, end: Vector2): void {
    const dist = Vec2.distance(start, end);
    const count = Math.floor(dist / 20);
    
    for (let i = 0; i < count; i++) {
      const t = i / count;
      const pos = Vec2.lerp(start, end, t);
      this.emit({
        count: 2,
        position: pos,
        spread: Math.PI * 2,
        speed: { min: 20, max: 50 },
        size: { min: 2, max: 4 },
        life: { min: 0.1, max: 0.3 },
        colors: [COLORS.CYAN, COLORS.MAGENTA],
        type: ParticleType.Energy,
      });
    }
  }

  /** Shockwave expanding ring effect */
  shockwaveEffect(position: Vector2, radius: number, color: string = COLORS.CYAN): void {
    const particleCount = Math.floor(radius / 5);
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const edgePos = {
        x: position.x + Math.cos(angle) * radius * 0.8,
        y: position.y + Math.sin(angle) * radius * 0.8,
      };
      
      this.emit({
        count: 1,
        position: edgePos,
        direction: angle,
        spread: 0.2,
        speed: { min: 50, max: 100 },
        size: { min: 4, max: 8 },
        life: { min: 0.2, max: 0.4 },
        colors: [color, '#ffffff'],
        type: ParticleType.Energy,
      });
    }
  }

  /** Shield/barrier effect */
  shieldEffect(position: Vector2, color: string = '#00aaff'): void {
    this.emit({
      count: 15,
      position,
      spread: Math.PI * 2,
      speed: { min: 20, max: 60 },
      size: { min: 3, max: 6 },
      life: { min: 0.5, max: 1.0 },
      colors: [color, '#ffffff'],
      type: ParticleType.Energy,
    });
  }

  /** Cloak/invisibility shimmer */
  cloakEffect(position: Vector2): void {
    this.emit({
      count: 12,
      position,
      spread: Math.PI * 2,
      speed: { min: 10, max: 40 },
      size: { min: 4, max: 8 },
      life: { min: 0.3, max: 0.6 },
      colors: ['#4444ff', '#8888ff', '#aaaaff'],
      type: ParticleType.Glitch,
    });
  }

  /** Reveal/scan pulse effect */
  revealEffect(position: Vector2, radius: number): void {
    this.shockwaveEffect(position, radius, COLORS.YELLOW);
    
    this.emit({
      count: 8,
      position,
      spread: Math.PI * 2,
      speed: { min: 30, max: 80 },
      size: { min: 3, max: 5 },
      life: { min: 0.4, max: 0.8 },
      colors: [COLORS.YELLOW, '#ffaa00'],
      type: ParticleType.Data,
    });
  }

  /** Healing burst effect */
  healBurstEffect(position: Vector2, color: string = '#00ff88'): void {
    // Rising heal particles
    this.emit({
      count: 20,
      position,
      spread: Math.PI * 2,
      speed: { min: 40, max: 100 },
      size: { min: 3, max: 6 },
      life: { min: 0.5, max: 1.0 },
      colors: [color, '#00ffaa', '#88ffaa'],
      type: ParticleType.Energy,
      gravity: -100, // Float upward
    });
    
    // Cross/plus symbols (energy particles)
    this.emit({
      count: 8,
      position,
      spread: Math.PI * 2,
      speed: { min: 20, max: 60 },
      size: { min: 6, max: 10 },
      life: { min: 0.6, max: 1.2 },
      colors: [color, '#ffffff'],
      type: ParticleType.Data,
      gravity: -50,
    });
  }

  /** Poison cloud effect */
  poisonCloudEffect(position: Vector2, radius: number): void {
    const count = Math.floor(radius / 8);
    
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.8;
      const particlePos = {
        x: position.x + Math.cos(angle) * dist,
        y: position.y + Math.sin(angle) * dist,
      };
      
      this.emit({
        count: 2,
        position: particlePos,
        spread: Math.PI * 2,
        speed: { min: 5, max: 20 },
        size: { min: 8, max: 16 },
        life: { min: 0.8, max: 1.5 },
        colors: ['#44ff44', '#22aa22', '#116611'],
        type: ParticleType.Smoke,
        gravity: -30, // Slowly rise
      });
    }
  }

  /** Fire/explosion effect */
  explosionEffect(position: Vector2, radius: number, color: string = '#ff4400'): void {
    // Scale particle count based on radius
    const scale = Math.max(1, radius / 50);
    
    // Core explosion
    this.emit({
      count: Math.floor(30 * scale),
      position,
      spread: Math.PI * 2,
      speed: { min: 100, max: 300 },
      size: { min: 4, max: 12 },
      life: { min: 0.2, max: 0.5 },
      colors: [color, '#ff8800', '#ffff00', '#ffffff'],
      type: ParticleType.Spark,
      gravity: 100,
    });
    
    // Smoke
    this.emit({
      count: Math.floor(15 * scale),
      position,
      spread: Math.PI * 2,
      speed: { min: 30, max: 80 },
      size: { min: 10, max: 20 },
      life: { min: 0.5, max: 1.0 },
      colors: ['#333333', '#444444', '#222222'],
      type: ParticleType.Smoke,
      gravity: -50,
    });
    
    // Debris
    this.emit({
      count: Math.floor(10 * scale),
      position,
      spread: Math.PI * 2,
      speed: { min: 150, max: 400 },
      size: { min: 2, max: 5 },
      life: { min: 0.3, max: 0.7 },
      colors: ['#666666', '#888888', '#444444'],
      type: ParticleType.Debris,
      gravity: 400,
    });
  }

  /** Grenade arc trail */
  grenadeTrail(position: Vector2): void {
    this.emit({
      count: 3,
      position,
      spread: Math.PI * 2,
      speed: { min: 10, max: 30 },
      size: { min: 2, max: 4 },
      life: { min: 0.1, max: 0.2 },
      colors: ['#888888', '#666666'],
      type: ParticleType.Smoke,
    });
  }

  /** Turret spawn effect */
  turretSpawnEffect(position: Vector2): void {
    this.emit({
      count: 20,
      position,
      spread: Math.PI * 2,
      speed: { min: 50, max: 150 },
      size: { min: 3, max: 6 },
      life: { min: 0.3, max: 0.6 },
      colors: [COLORS.CYAN, '#00ffaa', '#ffffff'],
      type: ParticleType.Energy,
    });
    
    // Assembly sparks
    this.emit({
      count: 10,
      position,
      spread: Math.PI * 2,
      speed: { min: 100, max: 200 },
      size: { min: 1, max: 3 },
      life: { min: 0.1, max: 0.3 },
      colors: [COLORS.YELLOW, '#ffaa00'],
      type: ParticleType.Spark,
      gravity: 200,
    });
  }

  /** Dash slash effect */
  dashSlashEffect(position: Vector2, direction: number, color: string = COLORS.CYAN): void {
    // Arc of energy
    for (let i = -3; i <= 3; i++) {
      const angle = direction + (i * 0.15);
      const edgePos = {
        x: position.x + Math.cos(angle) * 40,
        y: position.y + Math.sin(angle) * 40,
      };
      
      this.emit({
        count: 3,
        position: edgePos,
        direction: angle,
        spread: 0.3,
        speed: { min: 100, max: 200 },
        size: { min: 3, max: 6 },
        life: { min: 0.1, max: 0.25 },
        colors: [color, '#ffffff'],
        type: ParticleType.Energy,
      });
    }
  }

  /** Dome shield ambient particles */
  domeShieldEffect(position: Vector2, radius: number, color: string = '#4488ff'): void {
    // Particles along the dome edge
    const count = 12;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const edgePos = {
        x: position.x + Math.cos(angle) * radius,
        y: position.y + Math.sin(angle) * radius,
      };
      
      this.emit({
        count: 2,
        position: edgePos,
        direction: angle + Math.PI / 2,
        spread: 0.5,
        speed: { min: 20, max: 50 },
        size: { min: 3, max: 6 },
        life: { min: 0.4, max: 0.8 },
        colors: [color, '#ffffff'],
        type: ParticleType.Energy,
      });
    }
  }

  /** Fire zone continuous effect */
  fireZoneEffect(position: Vector2, radius: number): void {
    const count = Math.floor(radius / 15);
    
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.8;
      const particlePos = {
        x: position.x + Math.cos(angle) * dist,
        y: position.y + Math.sin(angle) * dist,
      };
      
      this.emit({
        count: 2,
        position: particlePos,
        spread: 0.5,
        speed: { min: 30, max: 80 },
        size: { min: 4, max: 10 },
        life: { min: 0.2, max: 0.5 },
        colors: ['#ff4400', '#ff8800', '#ffaa00'],
        type: ParticleType.Spark,
        gravity: -100, // Flames rise
      });
    }
  }

  /** Stun/EMP effect */
  stunEffect(position: Vector2): void {
    this.emit({
      count: 15,
      position,
      spread: Math.PI * 2,
      speed: { min: 50, max: 150 },
      size: { min: 2, max: 5 },
      life: { min: 0.2, max: 0.4 },
      colors: ['#ffff00', '#ffffff', '#ffff88'],
      type: ParticleType.Spark,
    });
    
    // Electric arcs (glitch particles)
    this.emit({
      count: 8,
      position,
      spread: Math.PI * 2,
      speed: { min: 20, max: 60 },
      size: { min: 4, max: 8 },
      life: { min: 0.3, max: 0.6 },
      colors: ['#00ffff', '#88ffff'],
      type: ParticleType.Glitch,
    });
  }

  /** Marked target indicator */
  markedEffect(position: Vector2): void {
    this.emit({
      count: 6,
      position,
      spread: Math.PI * 2,
      speed: { min: 10, max: 30 },
      size: { min: 3, max: 5 },
      life: { min: 0.5, max: 1.0 },
      colors: ['#ff0000', '#ff4444'],
      type: ParticleType.Energy,
      gravity: -20,
    });
  }

  clear(): void {
    this.particles = [];
  }

  getParticleCount(): number {
    return this.particles.length;
  }

  private colorWithAlpha(hex: string, alpha: number): string {
    // Handle hex colors
    if (hex.startsWith('#')) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // Handle existing rgba
    if (hex.startsWith('rgba')) {
      return hex.replace(/[\d.]+\)$/, `${alpha})`);
    }
    return hex;
  }
}
